import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/hooks/useAuth";
import ProtectedRoute from "@/components/ProtectedRoute";
import DashboardLayout from "@/components/DashboardLayout";

// Lazy-loaded page components for code splitting
const Index = lazy(() => import("./pages/Index"));
const Login = lazy(() => import("./pages/Login"));
const Register = lazy(() => import("./pages/Register"));
const RegisterFournisseur = lazy(() => import("./pages/RegisterFournisseur"));
const RegisterDropshipper = lazy(() => import("./pages/RegisterDropshipper"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const CompleteProfile = lazy(() => import("./pages/CompleteProfile"));
const SubscriptionCallback = lazy(() => import("./pages/SubscriptionCallback"));
const FournisseurDashboard = lazy(() => import("./pages/fournisseur/Dashboard"));
const ProductList = lazy(() => import("./pages/fournisseur/ProductList"));
const ProductForm = lazy(() => import("./pages/fournisseur/ProductForm"));
const ProductEdit = lazy(() => import("./pages/fournisseur/ProductEdit"));
const OrderList = lazy(() => import("./pages/fournisseur/OrderList"));
const FournisseurWallet = lazy(() => import("./pages/fournisseur/WalletPage"));
const FournisseurStatistics = lazy(() => import("./pages/fournisseur/Statistics"));
const Catalogue = lazy(() => import("./pages/dropshipper/Catalogue"));
const ProductDetail = lazy(() => import("./pages/dropshipper/ProductDetail"));
const AffiliateLinks = lazy(() => import("./pages/dropshipper/AffiliateLinks"));
const SalesPage = lazy(() => import("./pages/dropshipper/SalesPage"));
const DropshipperWallet = lazy(() => import("./pages/dropshipper/WalletPage"));
const DropshipperStatistics = lazy(() => import("./pages/dropshipper/Statistics"));
const ProductPage = lazy(() => import("./pages/public/ProductPage"));
const TermsPage = lazy(() => import("./pages/public/TermsPage"));
const PrivacyPage = lazy(() => import("./pages/public/PrivacyPage"));
const AdminDashboard = lazy(() => import("./pages/admin/Dashboard"));
const AdminUserList = lazy(() => import("./pages/admin/UserList"));
const AdminProductList = lazy(() => import("./pages/admin/ProductList"));
const AdminOrderList = lazy(() => import("./pages/admin/OrderList"));
const AdminTransactionList = lazy(() => import("./pages/admin/TransactionList"));
const AdminWithdrawList = lazy(() => import("./pages/admin/WithdrawList"));
const AdminStatistics = lazy(() => import("./pages/admin/Statistics"));
const AdminContactMessages = lazy(() => import("./pages/admin/ContactMessages"));
const AdminAuditLogs = lazy(() => import("./pages/admin/AuditLogs"));
const SettingsPage = lazy(() => import("./pages/shared/SettingsPage"));
const NotFound = lazy(() => import("./pages/NotFound"));

const queryClient = new QueryClient();

const FournisseurRoute = ({ children }: { children: React.ReactNode }) => (
  <ProtectedRoute allowedRoles={["fournisseur"]}><DashboardLayout>{children}</DashboardLayout></ProtectedRoute>
);
const DropshipperRoute = ({ children }: { children: React.ReactNode }) => (
  <ProtectedRoute allowedRoles={["dropshipper"]}><DashboardLayout>{children}</DashboardLayout></ProtectedRoute>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <AuthProvider>
          <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" /></div>}>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/register/fournisseur" element={<RegisterFournisseur />} />
              <Route path="/register/dropshipper" element={<RegisterDropshipper />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/complete-profile" element={<CompleteProfile />} />
              <Route path="/subscription-callback" element={<SubscriptionCallback />} />
              {/* Public product page */}
              <Route path="/p/:slug" element={<ProductPage />} />
              <Route path="/conditions" element={<TermsPage />} />
              <Route path="/confidentialite" element={<PrivacyPage />} />

              {/* Fournisseur */}
              <Route path="/fournisseur" element={<FournisseurRoute><FournisseurDashboard /></FournisseurRoute>} />
              <Route path="/fournisseur/produits" element={<FournisseurRoute><ProductList /></FournisseurRoute>} />
              <Route path="/fournisseur/produits/nouveau" element={<FournisseurRoute><ProductForm /></FournisseurRoute>} />
              <Route path="/fournisseur/produits/:id/modifier" element={<FournisseurRoute><ProductEdit /></FournisseurRoute>} />
              <Route path="/fournisseur/commandes" element={<FournisseurRoute><OrderList /></FournisseurRoute>} />
              <Route path="/fournisseur/wallet" element={<FournisseurRoute><FournisseurWallet /></FournisseurRoute>} />
              <Route path="/fournisseur/statistiques" element={<FournisseurRoute><FournisseurStatistics /></FournisseurRoute>} />
              <Route path="/fournisseur/parametres" element={<FournisseurRoute><SettingsPage /></FournisseurRoute>} />

              {/* Dropshipper */}
              <Route path="/dropshipper" element={<DropshipperRoute><Catalogue /></DropshipperRoute>} />
              <Route path="/dropshipper/produit/:id" element={<DropshipperRoute><ProductDetail /></DropshipperRoute>} />
              <Route path="/dropshipper/liens" element={<DropshipperRoute><AffiliateLinks /></DropshipperRoute>} />
              <Route path="/dropshipper/ventes" element={<DropshipperRoute><SalesPage /></DropshipperRoute>} />
              <Route path="/dropshipper/wallet" element={<DropshipperRoute><DropshipperWallet /></DropshipperRoute>} />
              <Route path="/dropshipper/statistiques" element={<DropshipperRoute><DropshipperStatistics /></DropshipperRoute>} />
              <Route path="/dropshipper/parametres" element={<DropshipperRoute><SettingsPage /></DropshipperRoute>} />

              {/* Admin */}
              <Route path="/admin" element={<ProtectedRoute allowedRoles={["admin"]}><DashboardLayout><AdminDashboard /></DashboardLayout></ProtectedRoute>} />
              <Route path="/admin/utilisateurs" element={<ProtectedRoute allowedRoles={["admin"]}><DashboardLayout><AdminUserList /></DashboardLayout></ProtectedRoute>} />
              <Route path="/admin/produits" element={<ProtectedRoute allowedRoles={["admin"]}><DashboardLayout><AdminProductList /></DashboardLayout></ProtectedRoute>} />
              <Route path="/admin/commandes" element={<ProtectedRoute allowedRoles={["admin"]}><DashboardLayout><AdminOrderList /></DashboardLayout></ProtectedRoute>} />
              <Route path="/admin/transactions" element={<ProtectedRoute allowedRoles={["admin"]}><DashboardLayout><AdminTransactionList /></DashboardLayout></ProtectedRoute>} />
              <Route path="/admin/retraits" element={<ProtectedRoute allowedRoles={["admin"]}><DashboardLayout><AdminWithdrawList /></DashboardLayout></ProtectedRoute>} />
              <Route path="/admin/statistiques" element={<ProtectedRoute allowedRoles={["admin"]}><DashboardLayout><AdminStatistics /></DashboardLayout></ProtectedRoute>} />
              <Route path="/admin/messages" element={<ProtectedRoute allowedRoles={["admin"]}><DashboardLayout><AdminContactMessages /></DashboardLayout></ProtectedRoute>} />
              <Route path="/admin/audit" element={<ProtectedRoute allowedRoles={["admin"]}><DashboardLayout><AdminAuditLogs /></DashboardLayout></ProtectedRoute>} />

              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
